<template>
  <div class="operation-report-show-body-form flex flex-col">
    <div class="form-title aisea-text-18-700-dark-gray" v-if="hasFormTitle">
      {{ formTitle }}
    </div>
    <operation-divider />

    <!-- グループベース描画（form_version_item_groups がある場合） -->
    <template v-if="hasGroups">
      <div class="form-items grid">
        <template v-for="groupEntry in groupedEntries">
          <!-- 可変行テーブル グループ（row_repeatable） -->
          <form-item-repeatable-table
            v-if="groupEntry.isRepeatable"
            :key="`repeatable-${groupEntry.group.id}`"
            :group="groupEntry.group"
            :items="groupEntry.items"
            :reportFormVersion="reportFormVersion"
            :isEditing="isEditing"
            :updatable="updatable"
            :reportFormVersionId="reportFormVersionId"
          />
          <!-- TABLE グループ -->
          <form-item-table
            v-else-if="groupEntry.isTable"
            :key="`group-${groupEntry.group.id}`"
            :group="groupEntry.group"
            :items="groupEntry.items"
            :reportFormVersionItemMapByFormVersionItemId="
              reportFormVersionItemMapByFormVersionItemId
            "
            :reportFormVersion="reportFormVersion"
            :isEditing="isEditing"
            :updatable="updatable"
            :reportFormVersionId="reportFormVersionId"
          />
          <!-- 可変行 × NORMAL グループ（group_type=NORMAL） -->
          <template v-else-if="groupEntry.isRepeatableCards">
            <div
              v-if="hasGroupLabel(groupEntry.group)"
              :key="`rep-cards-label-${groupEntry.group.id}`"
              class="form-item-group-title aisea-text-16-700-dark-gray"
            >
              {{ formatLabel(groupEntry.group.label) }}
            </div>
            <div
              v-for="row in groupEntry.repeatableRows"
              :key="`rep-row-${groupEntry.group.id}-${row.repeatIndex}`"
              class="repeatable-card-block"
            >
              <div class="repeatable-card-no aisea-text-14-700-dark-gray">
                {{ row.displayNo }}
              </div>
              <div class="repeatable-card">
                <div
                  v-for="cellProp in row.cells"
                  :key="cellProp.key"
                  class="operation-report-show-body-form-item-wrapper"
                  :class="`form-item-wrapper-${cellProp.key}`"
                >
                  <form-item-file
                    v-if="cellProp.isSingleImageFile"
                    :reportId="reportId"
                    v-bind="cellProp"
                  />
                  <operation-report-show-body-form-item
                    v-else
                    v-bind="cellProp"
                  />
                  <operation-divider
                    v-show="!isEditing && !isSpaceCell(cellProp)"
                  />
                </div>
              </div>
            </div>
          </template>
          <!-- NORMAL グループ / 未グループ項目（group=null） -->
          <template v-else>
            <!-- NORMAL グループのセクション見出し（未グループ項目は group=null のため非表示） -->
            <div
              v-if="hasGroupLabel(groupEntry.group)"
              :key="`normal-group-label-${groupEntry.group.id}`"
              class="form-item-group-title aisea-text-16-700-dark-gray"
            >
              {{ formatLabel(groupEntry.group.label) }}
            </div>
            <div
              v-for="formItemProp in groupEntry.formItemProps"
              :key="formItemProp.key"
              class="operation-report-show-body-form-item-wrapper"
              :class="`form-item-wrapper-${formItemProp.key}`"
            >
              <form-item-file
                v-if="formItemProp.isSingleImageFile"
                :reportId="reportId"
                v-bind="formItemProp"
              />
              <operation-report-show-body-form-item
                v-else
                v-bind="formItemProp"
              />
              <operation-divider
                v-show="!isEditing && !isSpaceCell(formItemProp)"
              />
            </div>
          </template>
        </template>
      </div>
    </template>

    <!-- 従来のフラット描画（後方互換） -->
    <template v-else>
      <div class="form-items grid">
        <div
          v-for="formItemProp in visibleFormItemProps"
          :key="formItemProp.key"
          class="operation-report-show-body-form-item-wrapper"
          :class="`form-item-wrapper-${formItemProp.key}`"
        >
          <form-item-file
            v-if="formItemProp.isSingleImageFile"
            :reportId="reportId"
            v-bind="formItemProp"
          />
          <operation-report-show-body-form-item v-else v-bind="formItemProp" />
          <operation-divider
            v-show="!isEditing && !isSpaceCell(formItemProp)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import mapKeys from "lodash/mapKeys";

import enterpriseFormService from "@/services/enterprises/enterpriseFormService";
import { calcNextFormItemValue } from "@/utils/operations";
import { createNamespacedHelpers } from "vuex";
const { mapGetters, mapActions } = createNamespacedHelpers("operationReport");

import OperationReportShowBodyFormItem from "./FormItem";
import FormItemFile from "./FormItemFile";
import FormItemTable from "./FormItemTable";
import FormItemRepeatableTable from "./FormItemTable/FormItemRepeatableTable";
import OperationDivider from "@/views/enterprise/addon/operation/components/OperationDivider";

export default {
  name: "OperationReportShowBodyForm",
  components: {
    OperationReportShowBodyFormItem,
    FormItemFile,
    FormItemTable,
    FormItemRepeatableTable,
    OperationDivider,
  },
  props: {
    reportId: {
      type: String,
      required: true,
    },
    // タイトル表示に使用
    operationWorkflowForm: {
      type: Object,
      default: () => ({}),
    },
    // 権限の判定に使用
    enterpriseForm: {
      type: Object,
      default: () => ({}),
    },
    // 権限の判定に使用
    enterpriseFormVersion: {
      type: Object,
      default: () => ({}),
    },
    // 表示用データ
    reportFormVersion: {
      type: Object,
      default: () => ({}),
    },
    // 編集権限があるかどうか
    updatable: {
      type: Boolean,
      default: false,
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    // 書き込み先 ao_enterprise_vessel_report_form_version.id
    reportFormVersionId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      lastSyncedSrcValues: {},
    };
  },

  computed: {
    ...mapGetters({
      globalReportFormVersionItemMap:
        "reportFormVersionItemMapByFormVersionItemId",
    }),

    defaultValueIdItems() {
      if (!Array.isArray(this.enterpriseFormVersionItems)) return [];
      return this.enterpriseFormVersionItems.filter(
        item =>
          item && item.default_value_id != null && item.default_value_id !== ""
      );
    },

    formTitle() {
      if (!isString(this.operationWorkflowForm.name)) return "";
      return this.operationWorkflowForm.name; // string
    },
    hasFormTitle() {
      return this.formTitle.length > 0;
    },
    enterpriseFormId() {
      if (!isString(this.enterpriseForm.id)) return "";
      return this.enterpriseForm.id; // string
    },
    enterpriseFormVersionId() {
      if (!isString(this.enterpriseFormVersion.id)) return "";
      return this.enterpriseFormVersion.id; // string
    },
    enterpriseFormVersionItems() {
      return this.enterpriseFormVersion.form_version_items;
    },
    reportFormVersionItemMapByFormVersionItemId() {
      const reportFormVersionItems = this.reportFormVersion
        .ao_enterprise_vessel_report_form_version_items;
      return mapKeys(reportFormVersionItems, "form_version_item_id");
    },

    // === グループベース描画 ===
    hasGroups() {
      return enterpriseFormService.hasFormVersionItemGroups(
        this.enterpriseFormVersion
      );
    },

    // グループごとのエントリ（TABLE / NORMAL を判定済み）
    groupedEntries() {
      const grouped = enterpriseFormService.getGroupedItems(
        this.enterpriseFormVersion
      );
      const {
        enterpriseFormId,
        enterpriseFormVersionId,
        isEditing,
        updatable,
        reportFormVersion,
        reportFormVersionItemMapByFormVersionItemId,
        reportFormVersionId,
      } = this;

      return grouped.map(({ group, items }) => {
        const isRepeatable = enterpriseFormService.isRepeatableGroup(group);
        const isTableGroupType = enterpriseFormService.isTableGroup(group);

        // group_type=TABLE: 固定 TABLE / 可変行テーブルとして表描画
        if (isTableGroupType) {
          return isRepeatable
            ? { group, items, isRepeatable: true }
            : { group, items, isTable: true };
        }

        if (isRepeatable) {
          const renderableCols = items.filter(item =>
            enterpriseFormService.isRenderableInReport(item)
          );
          const colIds = new Set(renderableCols.map(col => col.id));
          const reportItems =
            (reportFormVersion &&
              reportFormVersion.ao_enterprise_vessel_report_form_version_items) ||
            [];
          // repeat_index → (form_version_item_id → 値レコード)
          const itemByIndex = {};
          for (const reportItem of reportItems) {
            if (
              !reportItem ||
              !colIds.has(reportItem.form_version_item_id) ||
              reportItem.repeat_index == null
            )
              continue;
            if (!itemByIndex[reportItem.repeat_index])
              itemByIndex[reportItem.repeat_index] = {};
            itemByIndex[reportItem.repeat_index][
              reportItem.form_version_item_id
            ] = reportItem;
          }
          const repeatIndexes = Object.keys(itemByIndex)
            .map(Number)
            .sort((a, b) => a - b);
          // repeat_index ごとに「行」へまとめ、各行を 1 枚のカードとして描く（行カード表示）。
          const repeatableRows = repeatIndexes.map((repeatIndex, rowPos) => {
            const cells = renderableCols.map(enterpriseFormVersionItem => {
              const formVersionItemId = enterpriseFormVersionItem.id;
              const reportFormVersionItem = (itemByIndex[repeatIndex] &&
                itemByIndex[repeatIndex][formVersionItemId]) || {
                form_version_item_id: formVersionItemId,
                repeat_index: repeatIndex,
              };
              const key = `${enterpriseFormId}-${enterpriseFormVersionId}-${reportFormVersion}-${formVersionItemId}-${reportFormVersionItem.id}-r${repeatIndex}`;
              return {
                key,
                enterpriseFormVersionItem,
                reportFormVersion,
                reportFormVersionItem,
                isEditing,
                updatable,
                isSingleImageFile: enterpriseFormService.isSingleImageFile(
                  enterpriseFormVersionItem
                ),
                reportFormVersionId,
                repeatIndex,
              };
            });
            return { repeatIndex, displayNo: rowPos + 1, cells };
          });
          return { group, repeatableRows, isRepeatableCards: true };
        }

        // 通常 NORMAL グループ / 未グループ（group=null）: 既存の formItemProp 形式に変換
        const formItemProps = items
          .filter(enterpriseFormVersionItem =>
            enterpriseFormService.isRenderableInReport(
              enterpriseFormVersionItem
            )
          )
          .map(enterpriseFormVersionItem => {
            const formVersionItemId = enterpriseFormVersionItem.id;
            const reportFormVersionItem =
              reportFormVersionItemMapByFormVersionItemId[formVersionItemId] ||
              {};
            const reportFormVersionItemId = reportFormVersionItem.id;
            const key = `${enterpriseFormId}-${enterpriseFormVersionId}-${reportFormVersion}-${formVersionItemId}-${reportFormVersionItemId}`;
            const isSingleImageFile = enterpriseFormService.isSingleImageFile(
              enterpriseFormVersionItem
            );
            return {
              key,
              enterpriseFormVersionItem,
              reportFormVersion,
              reportFormVersionItem,
              isEditing,
              updatable,
              isSingleImageFile,
              reportFormVersionId,
            };
          });

        return { group, formItemProps, isTable: false };
      });
    },

    // === 従来のフラット描画（後方互換） ===
    formItemProps() {
      const {
        enterpriseFormId,
        enterpriseFormVersionId,
        isEditing,
        updatable,
        reportFormVersion,
        reportFormVersionItemMapByFormVersionItemId,
        reportFormVersionId,
      } = this;
      return this.enterpriseFormVersionItems.map(enterpriseFormVersionItem => {
        const formVersionItemId = enterpriseFormVersionItem.id;
        const reportFormVersionItem =
          reportFormVersionItemMapByFormVersionItemId[formVersionItemId] || {}; // reportFormVersionItem が undefined の場合にランタイムエラーになるのを防ぐ
        const reportFormVersionItemId = reportFormVersionItem.id;
        const key = `${enterpriseFormId}-${enterpriseFormVersionId}-${reportFormVersion}-${formVersionItemId}-${reportFormVersionItemId}`;
        const isSingleImageFile = enterpriseFormService.isSingleImageFile(
          enterpriseFormVersionItem
        );
        return {
          key,
          enterpriseFormVersionItem,
          reportFormVersion,
          reportFormVersionItem,
          isEditing,
          updatable,
          isSingleImageFile,
          reportFormVersionId,
        };
      });
    },

    // 表示対象のフォーム項目。描画しない type（SINGLE_OTHER_FILE / BINDING）のみ除外する
    visibleFormItemProps() {
      return this.formItemProps.filter(formItemProp =>
        enterpriseFormService.isRenderableInReport(
          formItemProp.enterpriseFormVersionItem
        )
      );
    },
  },

  methods: {
    ...mapActions({ setFormVersionItemValue: "setFormVersionItemValue" }),

    // SPACE はレイアウト調整用の不可視スペーサー。空セルなので区切り線（divider）は引かない
    isSpaceCell(formItemProp) {
      return (
        formItemProp != null &&
        enterpriseFormService.isSpace(formItemProp.enterpriseFormVersionItem)
      );
    },

    extractValue(reportFormVersionItem) {
      if (!reportFormVersionItem) return null;
      if (reportFormVersionItem.value_date != null)
        return reportFormVersionItem.value_date;
      if (reportFormVersionItem.value_string != null)
        return reportFormVersionItem.value_string;
      if (reportFormVersionItem.value_number != null)
        return reportFormVersionItem.value_number;
      return null;
    },

    autoSyncDefaultValueIdItems() {
      if (!this.isEditing) return;
      if (this.defaultValueIdItems.length === 0) return;
      for (const item of this.defaultValueIdItems) {
        const srcItem = this.globalReportFormVersionItemMap[
          String(item.default_value_id)
        ];
        if (!srcItem) continue;
        const srcValue = this.extractValue(srcItem);
        if (srcValue === null) continue;
        if (this.lastSyncedSrcValues[item.id] === srcValue) continue;
        this.lastSyncedSrcValues[item.id] = srcValue;
        const reportFormVersionItem =
          this.reportFormVersionItemMapByFormVersionItemId[String(item.id)] ||
          {};
        const currentValue = this.extractValue(reportFormVersionItem);
        if (currentValue === srcValue) continue;
        const nextFormItemValue = calcNextFormItemValue(
          item,
          reportFormVersionItem,
          { value: srcValue, nullableChecked: false }
        );
        this.setFormVersionItemValue({
          formVersionSortOrder: this.formVersionSortOrder,
          ...nextFormItemValue,
        });
      }
    },

    // グループ見出し用: label が文字列で非空のときだけ true（未グループ entry は group=null のため false）
    hasGroupLabel(group) {
      return isObject(group) && isString(group.label) && group.label.length > 0;
    },

    // JSON シリアライズ時にエスケープされた "\n" を実際の改行へ戻す（テーブル見出しと挙動を揃える）
    formatLabel(label) {
      if (!isString(label)) return label;
      return label.replace(/\\n/g, "\n");
    },
  },

  watch: {
    isEditing(newVal, oldVal) {
      if (!oldVal && newVal) {
        // 編集突入時: destination に既存値があるものは src 値を記録だけ行い、上書きしない（保護）
        this.lastSyncedSrcValues = {};
        for (const item of this.defaultValueIdItems) {
          const srcItem = this.globalReportFormVersionItemMap[
            String(item.default_value_id)
          ];
          const srcValue = this.extractValue(srcItem);
          if (srcValue === null) continue;
          const reportFormVersionItem =
            this.reportFormVersionItemMapByFormVersionItemId[String(item.id)] ||
            {};
          const currentValue = this.extractValue(reportFormVersionItem);
          if (currentValue != null && currentValue !== "") {
            this.lastSyncedSrcValues[item.id] = srcValue;
          }
        }
        this.$nextTick(() => this.autoSyncDefaultValueIdItems());
      }
    },
    globalReportFormVersionItemMap() {
      this.$nextTick(() => this.autoSyncDefaultValueIdItems());
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.operation-report-show-body-form {
  row-gap: 15px;

  .form-title {
    height: 32px;
  }

  .form-items {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 50px;
    row-gap: 0px;

    .form-item-group-title {
      grid-column: 1 / -1;
      margin: 8px 0;
    }

    .repeatable-card-block {
      grid-column: 1 / -1;
      margin-bottom: 16px;

      .repeatable-card-no {
        margin-bottom: 8px;
      }

      .repeatable-card {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 50px;
        row-gap: 0px;
        padding: 16px 20px;
        border: 1px solid $aisea-light-gray;
        border-radius: 8px;
      }
    }
  }
}
</style>
